import { Sparkles, Lock, Trophy } from "lucide-react";
import MasterySeal from "@/components/MasterySeal";

/**
 * BossQuizUnlockCard — Iter76 (extracted from BentoPath in Iter84).
 *
 * Three visual states driven by the `/boss-quiz/status` response:
 *  1. Sealed     → MasterySeal + best score, retake CTA.
 *  2. Locked     → gentle "finish 3 steps to unlock" panel.
 *  3. Eligible   → bold gradient "Take the Boss Quiz" CTA.
 *
 * Kept as a separate file because every state is a self-contained
 * layout with no shared state with BentoPath, and inlining it kept
 * pushing the parent file past 500 LOC.
 */
export default function BossQuizUnlockCard({ status, pathGoal, onOpen }) {
  if (status.has_seal) {
    return (
      <section
        className="rounded-2xl border border-[#FFD700]/22 bg-gradient-to-br from-[#1a1407]/80 to-[#0B0E14] px-5 py-4 flex items-center gap-4"
        data-testid="boss-quiz-seal-card"
      >
        <div className="shrink-0 scale-75 origin-left">
          <MasterySeal
            pathGoal={pathGoal}
            score={status.best_score}
            total={status.total}
            mintedAt={status.minted_at}
            variant="full"
          />
        </div>
        <div className="flex-1 min-w-0 pl-12 sm:pl-16">
          <div className="label-eyebrow text-[#FFD700] mb-1">Mastery sealed</div>
          <h3 className="font-display text-lg sm:text-xl text-white tracking-tight">
            You passed the Boss Quiz · {status.best_score}/{status.total}
          </h3>
          <p className="text-[12px] text-white/55 mt-1">
            Permanent record on your library cert wall. Re-take to refresh your understanding any time.
          </p>
          <button
            type="button"
            onClick={onOpen}
            className="mt-3 text-[11.5px] btn-mint-outline px-3 py-1.5 rounded-full inline-flex items-center gap-1.5"
            data-testid="boss-quiz-retake-cta"
          >
            <Sparkles size={11} /> Re-take for practice
          </button>
        </div>
      </section>
    );
  }

  if (!status.eligible) {
    return (
      <section
        className="rounded-2xl border border-white/8 bg-white/[0.02] px-5 py-4"
        data-testid="boss-quiz-locked-card"
      >
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 grid place-items-center rounded-xl bg-white/[0.04] border border-white/10 shrink-0">
            <Lock size={16} className="text-white/55" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="label-eyebrow text-white/55 mb-1">Boss Quiz · locked</div>
            <div className="text-[13px] text-white/80 font-display">
              Finish 3 steps&apos; deep dives to unlock the final mastery exam.
            </div>
            <div className="text-[11.5px] text-white/45 font-mono-oak mt-1">
              {status.completed_steps} / 3 steps completed
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      className="relative rounded-2xl border border-[#5D3FD3]/35 bg-gradient-to-br from-[#0044FF]/14 via-[#5D3FD3]/10 to-[#00FF9D]/8 px-5 py-4 overflow-hidden"
      data-testid="boss-quiz-unlock-card"
    >
      <div className="absolute -top-10 -right-10 h-32 w-32 rounded-full bg-[#FFD700]/12 blur-3xl pointer-events-none" aria-hidden />
      <div className="relative flex items-start gap-3">
        <div className="h-10 w-10 grid place-items-center rounded-xl bg-gradient-to-br from-[#0044FF]/22 to-[#5D3FD3]/22 border border-[#5D3FD3]/45 shrink-0">
          <Trophy size={16} className="text-[#bcd1ff]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="label-eyebrow text-[#9bc7ff] mb-1">Boss Quiz unlocked</div>
          <h3 className="font-display text-lg sm:text-xl text-white tracking-tight leading-snug">
            Take the final mastery exam
          </h3>
          <p className="text-[12px] text-white/55 mt-1">
            10 questions across every step · 80% pass · <span className="text-[#9bffcc]">+100 XP</span> and a Mastery Seal on success.
          </p>
        </div>
        <button
          type="button"
          onClick={onOpen}
          className="shrink-0 btn-primary px-4 py-2 rounded-full text-[12.5px] font-display font-semibold inline-flex items-center gap-1.5"
          data-testid="boss-quiz-open-cta"
        >
          <Sparkles size={12} /> Start
        </button>
      </div>
    </section>
  );
}
